<script setup lang="ts">
import { Comment, Fragment, computed, nextTick, onBeforeUnmount, ref, useAttrs, useSlots, watch, type StyleValue, type VNode } from 'vue';
import EGSPaginator from '@/components/ui/library/EGS_Paginator.vue';
import fbLoader from '@/components/fbLoader.vue';
import { getUserPreferences, validPageSize } from '@/composables/useUserPreferences';
import { formatTableDate } from '@/util/tableDateFormat';

defineOptions({ inheritAttrs: false, name: 'EGS_DataTable' });
type Row = Record<string, any>;
interface ColumnProps {
	field?: string;
	header?: string;
	sortable?: boolean;
	sortField?: string;
	selectionMode?: string;
	expander?: boolean;
	class?: unknown;
	style?: StyleValue;
	headerStyle?: StyleValue;
	bodyStyle?: StyleValue;
}
type ColumnSlots = Record<string, ((scope: Record<string, unknown>) => unknown) | undefined>;
type ColumnNode = VNode & { props: ColumnProps | null; children: ColumnSlots | null };
const props = withDefaults(
	defineProps<{
		value?: Row[];
		dataKey?: string;
		loading?: boolean;
		paginator?: boolean;
		rows?: number;
		first?: number;
		totalRecords?: number;
		lazy?: boolean;
		rowsPerPageOptions?: number[];
		currentPageReportTemplate?: string;
		stripedRows?: boolean;
		showGridlines?: boolean;
		sortField?: string | null;
		sortOrder?: number | null;
		removableSort?: boolean;
		filters?: Record<string, any>;
		globalFilterFields?: string[];
		selection?: Row[] | Row | null;
		expandedRows?: Row[] | Record<string, boolean> | null;
		rowStyle?: ((row: Row) => string | Record<string, string>) | string | Record<string, string>;
		rowClass?: ((row: Row) => string | Record<string, boolean>) | string | Record<string, boolean>;
		scrollable?: boolean;
		scrollHeight?: string;
		tableStyle?: string | Record<string, string>;
		size?: string;
	}>(),
	{
		value: () => [],
		dataKey: '',
		loading: false,
		paginator: true,
		rows: 10,
		first: 0,
		lazy: false,
		rowsPerPageOptions: () => [],
		currentPageReportTemplate: '{first} to {last} of {totalRecords}',
		stripedRows: false,
		showGridlines: false,
		sortField: null,
		sortOrder: null,
		removableSort: false,
		globalFilterFields: () => [],
		selection: null,
		expandedRows: null,
		scrollable: false,
		scrollHeight: '',
		size: ''
	}
);
const emit = defineEmits<{
	'update:first': [value: number];
	'update:selection': [value: Row[] | Row | null];
	'update:expandedRows': [value: Row[] | Record<string, boolean>];
	page: [event: { page: number; first: number; rows: number; pageCount: number }];
	sort: [event: { sortField: string | null; sortOrder: number; data: Row[] }];
	'row-click': [event: { data: Row; index: number; originalEvent: MouseEvent }];
	'row-expand': [event: { data: Row }];
}>();
const slots = useSlots();
const attrs = useAttrs();
const localFirst = ref(props.first);
const hoveredRow = ref<Row | null>(null);
const hoverCardRef = ref<HTMLElement | null>(null);
const hoverCardPosition = ref({ top: 0, left: 0 });
let hoverTrigger: HTMLElement | null = null;
let hoverCloseTimer: ReturnType<typeof setTimeout> | null = null;
// Lazy tables own their request size; client-side tables share the user's default.
const preferredRows = computed(() => (props.lazy ? props.rows : (validPageSize(getUserPreferences().pageSize) ?? props.rows)));
const localRows = ref(preferredRows.value);
const pageOptions = computed(() => [...new Set([...(props.rowsPerPageOptions.length ? props.rowsPerPageOptions : [10, 25, 50, 100]), localRows.value])].sort((a, b) => a - b));
const localSortField = ref<string | null>(props.sortField);
const localSortOrder = ref(props.sortOrder ?? 0);
watch(
	() => props.first,
	(value) => (localFirst.value = value)
);
watch(preferredRows, (value) => {
	localRows.value = value;
	localFirst.value = 0;
	emit('update:first', 0);
});
watch(
	() => props.sortField,
	(value) => (localSortField.value = value)
);
watch(
	() => props.sortOrder,
	(value) => (localSortOrder.value = value ?? 0)
);

const RenderContent = (renderProps: { render: () => unknown }) => renderProps.render();
function flatten(nodes: VNode[], result: VNode[] = []): VNode[] {
	for (const node of nodes) {
		if (node.type === Comment) continue;
		if (node.type === Fragment && Array.isArray(node.children)) flatten(node.children as VNode[], result);
		else result.push(node);
	}
	return result;
}
function tableColumns(): ColumnNode[] {
	return flatten((slots['default']?.() ?? []) as VNode[]).filter((node) => typeof node.type === 'object' && (node.type as { name?: string }).name === 'EGS_Column') as ColumnNode[];
}
function getPath(row: Row, path?: string): unknown {
	if (!path) return '';
	return path.split('.').reduce<unknown>((value, key) => (value != null && typeof value === 'object' ? (value as Row)[key] : undefined), row);
}
function rowKey(row: Row, index = 0): unknown {
	return props.dataKey ? getPath(row, props.dataKey) : (row ?? index);
}
function comparable(value: unknown): string | number {
	if (value == null) return '';
	if (typeof value === 'number') return value;
	const timestamp = Date.parse(String(value));
	return Number.isNaN(timestamp) ? String(value).toLocaleLowerCase() : timestamp;
}
const filteredRows = computed(() => {
	if (props.lazy) return props.value;
	let rows = [...props.value];
	const globalValue = String(props.filters?.['global']?.value ?? '')
		.trim()
		.toLocaleLowerCase();
	if (globalValue && props.globalFilterFields.length)
		rows = rows.filter((row) =>
			props.globalFilterFields.some((field) =>
				String(getPath(row, field) ?? '')
					.toLocaleLowerCase()
					.includes(globalValue)
			)
		);
	for (const [field, filter] of Object.entries(props.filters ?? {})) {
		if (field === 'global') continue;
		const value = String(filter?.value ?? '')
			.trim()
			.toLocaleLowerCase();
		if (value)
			rows = rows.filter((row) =>
				String(getPath(row, field) ?? '')
					.toLocaleLowerCase()
					.includes(value)
			);
	}
	if (localSortField.value && localSortOrder.value) {
		const direction = localSortOrder.value;
		rows.sort((left, right) => {
			const a = comparable(getPath(left, localSortField.value ?? ''));
			const b = comparable(getPath(right, localSortField.value ?? ''));
			return (a < b ? -1 : a > b ? 1 : 0) * direction;
		});
	}
	return rows;
});
const total = computed(() => props.totalRecords ?? filteredRows.value.length);
watch(total, (count) => {
	if (!props.lazy && localFirst.value >= count) {
		localFirst.value = 0;
		emit('update:first', 0);
	}
});
const visibleRows = computed(() => (props.paginator && !props.lazy ? filteredRows.value.slice(localFirst.value, localFirst.value + localRows.value) : filteredRows.value));
const compactCell = computed(() => (props.size === 'small' ? 'px-3 py-2' : 'px-4 py-3'));
function sort(column: ColumnNode) {
	if (!column.props?.sortable) return;
	const field = column.props.sortField || column.props.field || null;
	let order = localSortField.value === field ? (localSortOrder.value === 1 ? -1 : props.removableSort && localSortOrder.value === -1 ? 0 : 1) : 1;
	localSortField.value = order ? field : null;
	localSortOrder.value = order;
	emit('sort', { sortField: localSortField.value, sortOrder: order, data: filteredRows.value });
}
function page(event: { page: number; first: number; rows: number; pageCount: number }) {
	localFirst.value = event.first;
	localRows.value = event.rows;
	emit('update:first', event.first);
	emit('page', event);
}
function isSelected(row: Row): boolean {
	const selection = props.selection;
	if (Array.isArray(selection)) return selection.some((item) => Object.is(rowKey(item), rowKey(row)));
	return !!selection && Object.is(rowKey(selection), rowKey(row));
}
let selectionAnchor: Row | null = null;
watch(visibleRows, (rows) => {
	const anchor = selectionAnchor;
	if (anchor && !rows.some((row) => Object.is(rowKey(row), rowKey(anchor)))) selectionAnchor = null;
});
watch(
	() => props.selection,
	(selection) => {
		if (!Array.isArray(selection) || selection.length === 0) selectionAnchor = null;
	}
);
function toggleSelection(row: Row, checked: boolean, shiftKey: boolean) {
	const selection = Array.isArray(props.selection) ? [...props.selection] : [];
	const anchor = selectionAnchor;
	const anchorIndex = anchor ? visibleRows.value.findIndex((item) => Object.is(rowKey(item), rowKey(anchor))) : -1;
	const rowIndex = visibleRows.value.findIndex((item) => Object.is(rowKey(item), rowKey(row)));
	const range = shiftKey && anchorIndex >= 0 && rowIndex >= 0 ? visibleRows.value.slice(Math.min(anchorIndex, rowIndex), Math.max(anchorIndex, rowIndex) + 1) : [row];
	for (const item of range) {
		const index = selection.findIndex((selected) => Object.is(rowKey(selected), rowKey(item)));
		if (checked && index < 0) selection.push(item);
		if (!checked && index >= 0) selection.splice(index, 1);
	}
	// Keep the starting row fixed while extending a range with Shift.
	if (!shiftKey || anchorIndex < 0) selectionAnchor = row;
	emit('update:selection', selection);
}
function toggleAll(checked: boolean) {
	selectionAnchor = null;
	const visibleKeys = new Set(visibleRows.value.map((row, index) => rowKey(row, index)));
	const current = Array.isArray(props.selection) ? props.selection.filter((row) => !visibleKeys.has(rowKey(row))) : [];
	emit('update:selection', checked ? [...current, ...visibleRows.value] : current);
}
const allSelected = computed(() => visibleRows.value.length > 0 && visibleRows.value.every(isSelected));
function isExpanded(row: Row): boolean {
	if (Array.isArray(props.expandedRows)) return props.expandedRows.some((item) => Object.is(rowKey(item), rowKey(row)));
	return !!props.expandedRows && Boolean(props.expandedRows[String(rowKey(row))]);
}
function toggleExpansion(row: Row) {
	const expanded = !isExpanded(row);
	if (Array.isArray(props.expandedRows)) {
		const next = props.expandedRows.filter((item) => !Object.is(rowKey(item), rowKey(row)));
		if (expanded) next.push(row);
		emit('update:expandedRows', next);
	} else {
		const next = { ...(props.expandedRows ?? {}) };
		if (expanded) next[String(rowKey(row))] = true;
		else delete next[String(rowKey(row))];
		emit('update:expandedRows', next);
	}
	if (expanded) emit('row-expand', { data: row });
}
function body(column: ColumnNode, row: Row, index: number) {
	const value = getPath(row, column.props?.field);
	return column.children?.['body']?.({ data: row, field: column.props?.field, index }) ?? (value instanceof Date ? formatTableDate(value) : String(value ?? ''));
}
function rowClick(row: Row, index: number, event: MouseEvent) {
	if ((event.target as HTMLElement).closest('button,a,input,select,textarea,label')) return;
	emit('row-click', { data: row, index, originalEvent: event });
}

function clearRowHoverCloseTimer() {
	if (!hoverCloseTimer) return;
	clearTimeout(hoverCloseTimer);
	hoverCloseTimer = null;
}

function updateRowHoverPosition() {
	if (!hoverTrigger || !hoverCardRef.value) return;

	const triggerRect = hoverTrigger.getBoundingClientRect();
	const cardRect = hoverCardRef.value.getBoundingClientRect();
	const viewportPadding = 8;
	const offset = 12;
	let left = triggerRect.right + offset;
	if (left + cardRect.width > window.innerWidth - viewportPadding) {
		left = triggerRect.left - cardRect.width - offset;
	}
	left = Math.max(viewportPadding, Math.min(left, window.innerWidth - cardRect.width - viewportPadding));

	let top = triggerRect.top + triggerRect.height / 2 - cardRect.height / 2;
	top = Math.max(viewportPadding, Math.min(top, window.innerHeight - cardRect.height - viewportPadding));
	hoverCardPosition.value = { top, left };
}

function closeRowHover() {
	clearRowHoverCloseTimer();
	hoveredRow.value = null;
	hoverTrigger = null;
}

function scheduleRowHoverClose() {
	clearRowHoverCloseTimer();
	hoverCloseTimer = setTimeout(closeRowHover, 150);
}

function isRowHoverIgnored(target: EventTarget | null) {
	return target instanceof Element && !!target.closest('button,a,input,select,textarea,label,[data-row-hover-ignore]');
}

function openRowHover(row: Row, event: PointerEvent) {
	if (!slots['row-hover-content']) return;
	if (isRowHoverIgnored(event.target)) {
		scheduleRowHoverClose();
		return;
	}

	clearRowHoverCloseTimer();
	hoveredRow.value = row;
	hoverTrigger = event.currentTarget as HTMLElement;
	nextTick(updateRowHoverPosition);
}

onBeforeUnmount(closeRowHover);
</script>

<template>
	<div v-bind="attrs" :class="['egs-datatable relative w-full text-color', attrs['class']]" :aria-busy="loading">
		<div v-if="$slots['header']" class="egs-datatable-header border-b border-surface bg-[var(--egs-content-background)] p-4"><slot name="header" /></div>
		<div :class="['w-full', scrollable ? 'overflow-auto' : 'overflow-x-auto']" :style="scrollHeight ? { maxHeight: scrollHeight } : undefined">
			<table class="w-full border-collapse text-left text-sm" :style="tableStyle">
				<thead class="egs-datatable-thead bg-[var(--egs-content-hover)]">
					<tr class="border-b border-surface">
						<th
							v-for="(column, columnIndex) in tableColumns()"
							:key="column.props?.field || column.props?.header || columnIndex"
							:class="[
								'border-b border-surface text-left text-xs font-semibold uppercase tracking-wide text-color-secondary',
								compactCell,
								column.props?.sortable ? 'cursor-pointer select-none hover:bg-surface-200/70 dark:hover:bg-surface-700/70' : '',
								column.props?.class
							]"
							:style="column.props?.headerStyle || column.props?.style"
							@click="sort(column)"
						>
							<input
								v-if="column.props?.selectionMode === 'multiple'"
								type="checkbox"
								class="h-4 w-4 accent-primary-600"
								:checked="allSelected"
								aria-label="Select all rows"
								@click.stop
								@change="toggleAll(($event.target as HTMLInputElement).checked)"
							/>
							<span v-else class="flex items-center gap-2"
								><span>{{ column.props?.header }}</span
								><font-awesome-icon
									v-if="column.props?.sortable"
									:icon="localSortField === (column.props?.sortField || column.props?.field) ? (localSortOrder === -1 ? 'sort-down' : 'sort-up') : 'sort'"
									class="h-3.5 w-3.5 text-color-secondary"
							/></span>
						</th>
					</tr>
				</thead>
				<tbody v-if="loading" class="egs-datatable-tbody bg-[var(--egs-content-background)]">
					<tr>
						<td :colspan="Math.max(1, tableColumns().length)" class="p-0 text-center">
							<div role="status" class="flex min-h-40 items-center justify-center px-4 py-4 text-color-secondary">
								<slot name="loading">
									<fbLoader size="40px" />
									<span class="sr-only">Loading records...</span>
								</slot>
							</div>
						</td>
					</tr>
				</tbody>
				<tbody v-else class="egs-datatable-tbody divide-y divide-[var(--egs-content-border)] bg-[var(--egs-content-background)]">
					<template v-for="(row, rowIndex) in visibleRows" :key="rowKey(row, rowIndex)">
						<tr
							:class="[
								isSelected(row) ? 'bg-primary-50 dark:bg-primary-950/40' : ['hover:bg-[var(--egs-content-hover)]', stripedRows && rowIndex % 2 === 1 ? 'bg-surface-100/70 dark:bg-surface-700/40' : ''],
								typeof rowClass === 'function' ? rowClass(row) : rowClass
							]"
							:style="typeof rowStyle === 'function' ? rowStyle(row) : rowStyle"
							@click="rowClick(row, rowIndex, $event)"
							@pointerover="openRowHover(row, $event)"
							@pointerleave="scheduleRowHoverClose"
						>
							<td
								v-for="(column, columnIndex) in tableColumns()"
								:key="column.props?.field || column.props?.header || columnIndex"
								:class="['align-middle text-color', compactCell, showGridlines ? 'border border-surface' : '', column.props?.class]"
								:style="column.props?.bodyStyle || column.props?.style"
							>
								<input
									v-if="column.props?.selectionMode === 'multiple'"
									type="checkbox"
									class="h-4 w-4 accent-primary-600"
									:checked="isSelected(row)"
									aria-label="Select row"
									@click.stop="toggleSelection(row, ($event.target as HTMLInputElement).checked, $event.shiftKey)"
								/>
								<button
									v-else-if="column.props?.expander"
									type="button"
									class="inline-flex h-8 w-8 items-center justify-center rounded-full text-color-secondary hover:bg-[var(--egs-content-hover)]"
									:aria-label="isExpanded(row) ? 'Collapse row' : 'Expand row'"
									@click.stop="toggleExpansion(row)"
								>
									<font-awesome-icon :icon="isExpanded(row) ? 'chevron-down' : 'chevron-right'" />
								</button>
								<RenderContent v-else :render="() => body(column, row, rowIndex)" />
							</td>
						</tr>
						<tr v-if="isExpanded(row) && $slots['expansion']">
							<td :colspan="tableColumns().length" class="border-t border-surface bg-[var(--egs-content-hover)] p-0"><RenderContent :render="() => $slots['expansion']?.({ data: row })" /></td>
						</tr>
					</template>
					<tr v-if="visibleRows.length === 0">
						<td :colspan="Math.max(1, tableColumns().length)" class="px-4 py-10 text-center text-color-secondary"><slot name="empty">No records found.</slot></td>
					</tr>
				</tbody>
				<tfoot v-if="$slots['footer']">
					<tr>
						<td :colspan="Math.max(1, tableColumns().length)" class="border-t border-surface bg-[var(--egs-content-hover)] px-4 py-3 text-sm text-color-secondary"><slot name="footer" /></td>
					</tr>
				</tfoot>
			</table>
		</div>
		<Teleport to="body">
			<Transition name="hovercard-fade">
				<div
					v-if="hoveredRow"
					ref="hoverCardRef"
					class="hovercard-content fixed z-[9999] min-w-[280px] max-w-[340px] rounded-lg border border-surface bg-[var(--egs-content-background)] p-4 text-color shadow-lg pointer-events-auto"
					role="dialog"
					aria-modal="false"
					:style="{ top: hoverCardPosition.top + 'px', left: hoverCardPosition.left + 'px' }"
					@pointerenter="clearRowHoverCloseTimer"
					@pointerleave="scheduleRowHoverClose"
				>
					<slot name="row-hover-content" :data="hoveredRow" />
				</div>
			</Transition>
		</Teleport>
		<EGSPaginator v-if="paginator && total > Math.min(...pageOptions)" :first="localFirst" :rows="localRows" :totalRecords="total" :rowsPerPageOptions="pageOptions" :currentPageReportTemplate="currentPageReportTemplate" @page="page" />
	</div>
</template>
